import { useEffect, useState } from 'react'
import { Check, Lightbulb, Loader2, Sparkles } from 'lucide-react'
import type { MaintenanceProgress } from './types'

// 분석 대기 중 보여줄 4단계. 백엔드 진행 이벤트가 없어 타이머로 순차 연출한다.
const ANALYSIS_STEPS = [
    '변경 내용 해석',
    '관련 평가항목 검토',
    '영향·근거 분석',
    '결과 정리',
] as const

// 변경관리 도메인 팁 — 수십 초 대기 중 지루함을 줄이고 맥락을 환기한다(과하지 않게 1개씩 로테이션).
const HACCP_TIPS = [
    '설비·공정 변경은 CCP와 선행요건 프로그램에 함께 영향을 줄 수 있어요.',
    '원료나 공급업체가 바뀌면 위해요소 분석을 다시 살펴보는 게 좋아요.',
    '가열·냉각 한계기준이 달라지면 모니터링·검증 방법도 같이 점검하세요.',
]

const STEP_INTERVAL_MS = 2600
const TIP_INTERVAL_MS = 5200

/**
 * 변경 영향 분석 대기 화면 — 단계별 진행 인디케이터 + 분석 대상 미리보기 + 도메인 팁.
 * 실제 진행 이벤트가 없어 타이머 기반으로 단계를 순차 활성화하고, 마지막 단계(결과 정리)는
 * 분석이 끝나(progress=null) 언마운트될 때까지 유지한다.
 */
export function AnalysisProgress({
    progress,
    changeDescription,
}: {
    progress: MaintenanceProgress
    changeDescription: string | null
}) {
    const [activeStep, setActiveStep] = useState(0)
    const [tipIndex, setTipIndex] = useState(0)
    // 스트리밍으로 실제 영향 항목이 도착하기 시작하면 마지막 단계(결과 정리)로 넘긴다.
    const streaming =
        typeof progress.affectedCount === 'number' && progress.affectedCount > 0

    useEffect(() => {
        const t = setInterval(() => {
            setActiveStep((p) => Math.min(ANALYSIS_STEPS.length - 1, p + 1))
        }, STEP_INTERVAL_MS)
        return () => clearInterval(t)
    }, [])

    useEffect(() => {
        if (streaming) setActiveStep(ANALYSIS_STEPS.length - 1)
    }, [streaming])

    useEffect(() => {
        const t = setInterval(() => {
            setTipIndex((p) => (p + 1) % HACCP_TIPS.length)
        }, TIP_INTERVAL_MS)
        return () => clearInterval(t)
    }, [])

    return (
        <div className='grid gap-3'>
            <div className='text-foreground flex items-center gap-2 text-sm font-medium'>
                <span className='bg-accent text-primary grid size-6 shrink-0 animate-pulse place-items-center rounded-full'>
                    <Sparkles className='size-3.5' />
                </span>
                {progress.title || '변경 내용을 분석하고 있어요'}
            </div>

            {streaming ? (
                <p
                    className='text-primary text-xs font-medium'
                    aria-live='polite'>
                    지금까지 {progress.affectedCount}개 영향 항목을 찾았어요…
                </p>
            ) : null}

            {/* 단계 변경 시점에만 간결하게 읽어 주는 보조 라이브 영역(시각 목록은 중복 안내 방지로 비활성). */}
            <p className='sr-only' aria-live='polite'>
                {ANALYSIS_STEPS.length}단계 중 {activeStep + 1}단계:{' '}
                {ANALYSIS_STEPS[activeStep]} 진행 중
            </p>

            {changeDescription ? (
                <div className='border-border bg-background rounded-sm border px-3 py-2'>
                    <p className='text-muted-foreground text-xs'>
                        이 변경을 분석하고 있어요
                    </p>
                    <p className='text-foreground mt-1 line-clamp-3 text-sm whitespace-pre-wrap'>
                        {changeDescription}
                    </p>
                </div>
            ) : null}

            <ol className='grid gap-2'>
                {ANALYSIS_STEPS.map((label, index) => {
                    const done = index < activeStep
                    const active = index === activeStep
                    return (
                        <li key={label} className='flex items-center gap-2.5'>
                            <span
                                className={`grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-semibold transition-colors ${
                                    done
                                        ? 'bg-primary text-primary-foreground'
                                        : active
                                          ? 'bg-accent text-primary'
                                          : 'bg-muted text-muted-foreground'
                                }`}>
                                {done ? (
                                    <Check className='size-3' />
                                ) : active ? (
                                    <Loader2 className='size-3 animate-spin' />
                                ) : (
                                    index + 1
                                )}
                            </span>
                            <span
                                className={`text-sm transition-colors ${
                                    active
                                        ? 'text-foreground font-medium'
                                        : done
                                          ? 'text-muted-foreground'
                                          : 'text-muted-foreground/70'
                                }`}>
                                {label}
                            </span>
                        </li>
                    )
                })}
            </ol>

            <div className='border-border bg-accent/40 text-muted-foreground flex items-start gap-2 rounded-sm border px-3 py-2 text-xs'>
                <Lightbulb className='text-primary mt-0.5 size-3.5 shrink-0' />
                <span
                    key={tipIndex}
                    className='animate-in fade-in duration-500'>
                    {HACCP_TIPS[tipIndex]}
                </span>
            </div>
        </div>
    )
}
